import {
  BrainIcon,
  Bug01Icon,
  File01Icon,
  GitBranchIcon,
  Search01Icon,
  Target03Icon,
  TerminalIcon,
} from '@terra/ui-icons';
import {
  formatTerminalPayload,
  memoryHitlConfig,
  renderAssetMergeHitlContent,
  renderBusinessContextCard,
  renderDiscoverySignalCard,
  renderDiscoverySignalResult,
  renderDocsSearchResult,
  renderFindingsCard,
  renderFindingsListResult,
  renderGetAdEntityDetailsCard,
  renderGetAdEntityDetailsResult,
  renderGetAdEntityRelationshipsCard,
  renderGetAdEntityRelationshipsResult,
  renderListAdEntitiesCard,
  renderListAdEntitiesResult,
  renderMemoryContent,
  renderMemoryEditContent,
  renderMemoryEditModal,
  renderMemoryListResult,
  renderMemoryRecallCard,
  renderMemoryResult,
  renderQueryAdGraphCard,
  renderQueryAdGraphResult,
  renderSignalsCard,
  renderSignalsListResult,
  renderTerminalResult,
} from './tools';
import { getTerminalDisplayInput, parseTerminalOutput } from './parsers';
import { renderTerminalCard } from './renderers';
import type { ToolConfig } from './tool-config-types';

export const buildDomainAndMemoryToolConfigs = (): Record<
  string,
  ToolConfig
> => ({
  'remote-c2-command': {
    name: 'Remote Command',
    icon: TerminalIcon,
    category: 'executable',
    cardType: 'minimal',
    contentType: 'terminal',
    runningText: 'Running remote command...',
    renderCard: renderTerminalCard,
    renderOutput: renderTerminalResult,
    formatPayload: formatTerminalPayload,
    parseOutput: parseTerminalOutput,
    getDisplayInput: getTerminalDisplayInput,
  },
  'list-ad-entities': {
    name: 'List AD Entities',
    icon: GitBranchIcon,
    category: 'intelligence',
    cardType: 'inline',
    contentType: 'ad-entities-list',
    runningText: 'Listing AD entities...',
    renderCard: renderListAdEntitiesCard,
    renderOutput: renderListAdEntitiesResult,
  },
  'get-ad-entity-details': {
    name: 'AD Entity',
    icon: GitBranchIcon,
    category: 'intelligence',
    cardType: 'inline',
    contentType: 'ad-entity',
    runningText: 'Fetching AD entity...',
    renderCard: renderGetAdEntityDetailsCard,
    renderOutput: renderGetAdEntityDetailsResult,
  },
  'get-ad-entity-relationships': {
    name: 'AD Relationships',
    icon: GitBranchIcon,
    category: 'intelligence',
    cardType: 'inline',
    runningText: 'Fetching AD relationships...',
    renderCard: renderGetAdEntityRelationshipsCard,
    renderOutput: renderGetAdEntityRelationshipsResult,
  },
  'query-ad-graph': {
    name: 'AD Graph',
    icon: GitBranchIcon,
    category: 'intelligence',
    cardType: 'inline',
    contentType: 'ad-graph',
    runningText: 'Querying AD graph...',
    renderCard: renderQueryAdGraphCard,
    renderOutput: renderQueryAdGraphResult,
  },
  'create-internal-network-discovery-signal': {
    name: 'Create Signal',
    icon: Target03Icon,
    category: 'intelligence',
    cardType: 'inline',
    runningText: 'Creating discovery signal...',
    renderCard: renderDiscoverySignalCard,
    renderOutput: renderDiscoverySignalResult,
  },
  'get-existing-internal-network-discovery-signals': {
    name: 'List Signals',
    icon: Target03Icon,
    category: 'intelligence',
    cardType: 'inline',
    runningText: 'Listing discovery signals...',
    renderCard: renderDiscoverySignalCard,
    renderOutput: renderDiscoverySignalResult,
  },
  'create-internal-network-scenario-draft': {
    name: 'Scenario Draft',
    icon: File01Icon,
    category: 'intelligence',
    cardType: 'inline',
    runningText: 'Creating scenario draft...',
    renderCard: renderDiscoverySignalCard,
    renderOutput: renderDiscoverySignalResult,
  },
  'signals-semantic-search-tool': {
    name: 'Search Signals',
    icon: Target03Icon,
    category: 'intelligence',
    cardType: 'inline',
    contentType: 'signals-list',
    runningText: 'Searching signals...',
    renderCard: renderSignalsCard,
    renderOutput: renderSignalsListResult,
  },
  'finding-semantic-search-tool': {
    name: 'Search Findings',
    icon: Bug01Icon,
    category: 'intelligence',
    cardType: 'inline',
    contentType: 'findings-list',
    runningText: 'Searching findings...',
    renderCard: renderFindingsCard,
    renderOutput: renderFindingsListResult,
  },
  'findings-semantic-search-tool': {
    name: 'Search Findings',
    icon: Bug01Icon,
    category: 'intelligence',
    cardType: 'inline',
    contentType: 'findings-list',
    runningText: 'Searching findings...',
    renderCard: renderFindingsCard,
    renderOutput: renderFindingsListResult,
  },
  'long-term-memory-tool': {
    name: 'Recall',
    icon: BrainIcon,
    category: 'intelligence',
    cardType: 'inline',
    runningText: 'Recalling memory...',
  },
  'merge-assets-tool': {
    name: 'Merge Assets',
    icon: GitBranchIcon,
    category: 'executable',
    cardType: 'minimal',
    runningText: 'Merging or revising assets...',
    renderContent: renderAssetMergeHitlContent,
  },
  'revert-merge-tool': {
    name: 'Revert asset merge',
    icon: GitBranchIcon,
    category: 'executable',
    cardType: 'minimal',
    runningText: 'Reverting merge...',
    renderContent: renderAssetMergeHitlContent,
  },
  'create-memory-tool': {
    name: 'Remember',
    icon: BrainIcon,
    category: 'intelligence',
    cardType: 'minimal',
    contentType: 'memory',
    hitlConfig: memoryHitlConfig,
    usesTabEdit: true,
    runningText: 'Saving to memory...',
    renderContent: renderMemoryContent,
    renderEditModal: renderMemoryEditModal,
    renderEditContent: renderMemoryEditContent,
    renderOutput: renderMemoryResult,
    hasExpandableContent: ({ status }) => status !== 'completed',
  },
  'get-memory-tool': {
    name: 'Recall',
    icon: BrainIcon,
    category: 'intelligence',
    cardType: 'inline',
    contentType: 'memory-list',
    runningText: 'Recalling memory...',
    renderCard: renderMemoryRecallCard,
    renderOutput: renderMemoryListResult,
  },
  get_neo_memories: {
    name: 'Recall',
    icon: BrainIcon,
    category: 'intelligence',
    cardType: 'inline',
    runningText: 'Recalling memory...',
    renderCard: renderMemoryRecallCard,
    renderOutput: renderMemoryListResult,
  },
  'business-context-tool': {
    name: 'Business Context',
    icon: File01Icon,
    category: 'intelligence',
    cardType: 'inline',
    renderCard: renderBusinessContextCard,
  },
  'application-scoping-tool': {
    name: 'App Scope',
    icon: File01Icon,
    category: 'intelligence',
    cardType: 'inline',
  },
  'docs-search-tool': {
    name: 'Docs Search',
    icon: Search01Icon,
    category: 'intelligence',
    cardType: 'inline',
    contentType: 'json',
    renderOutput: renderDocsSearchResult,
  },
});
